import type { CampaignAnalyticsGranularity } from "@adpilot/shared";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  type MouseHandlerDataParam,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { z } from "zod";
import {
  formatTrendPeriod,
  type PerformanceKpi,
} from "@/components/shared/performance/performance";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardPerformanceMetric, DashboardTrendPoint } from "@/lib/dashboard-sales";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { VendorMetricAvailability } from "@/lib/vendor-metric-availability";
import {
  dashboardMetricSchema,
  formatAxisTick,
  formatMetricValue,
  METRIC_FAMILY,
  niceTicks,
  PERFORMANCE_LOWER_METRICS,
  PERFORMANCE_VIEWS,
  type PerformanceView,
} from "./kpi-strip-metrics";

type Metric = DashboardPerformanceMetric;
type ChartRow = DashboardTrendPoint & {
  key: string;
  organicSales: number | null;
  organicOrders: number | null;
};

export interface PerformanceCardProps {
  trend: readonly DashboardTrendPoint[];
  /** Supplies each metric's currency and the period value shown as the lower panel's average. */
  kpis: readonly PerformanceKpi<Metric>[];
  view: PerformanceView;
  onViewChange: (view: PerformanceView) => void;
  /** Ratio drawn in the lower panel; one of the view's `PERFORMANCE_LOWER_METRICS`. */
  lowerMetric: Metric;
  onLowerMetricChange: (metric: Metric) => void;
  /** Workspace target ACoS (fraction), drawn as the ACoS reference line. */
  targetAcos?: number | null;
  granularity: CampaignAnalyticsGranularity;
  getLabel: (metric: Metric) => string;
  isLoading?: boolean;
  /** Inline notices (e.g. Ads disconnected, Sales retry) shown under the title. */
  notice?: React.ReactNode;
  /**
   * Scope availability of the Seller Central sales (#679). While Vendor Central is
   * pending, the chart draws the ad-attributed series alone (no organic stack, no
   * TACoS) rather than a stack whose total is unknown.
   */
  salesAvailability?: VendorMetricAvailability;
  className?: string;
}

interface StackSegment {
  key: "sales" | "organicSales" | "orders" | "organicOrders";
  color: string;
}

interface ViewConfig {
  /** Bars, bottom to top; the stack total is the view's headline metric. */
  stack: readonly StackSegment[];
  total: Metric;
  /** Ad spend rides along the Sales stack on the same currency axis. */
  line?: { key: Metric; color: string };
  lowerColor: string;
  lowerFill: string;
}

const VIEWS: Record<PerformanceView, ViewConfig> = {
  sales: {
    stack: [
      { key: "sales", color: "var(--color-chart-1)" },
      { key: "organicSales", color: "var(--color-brand-5)" },
    ],
    total: "totalSales",
    line: { key: "spend", color: "var(--color-chart-2)" },
    lowerColor: "var(--color-chart-3)",
    lowerFill: "var(--color-violet-3)",
  },
  orders: {
    stack: [
      { key: "orders", color: "var(--color-chart-4)" },
      { key: "organicOrders", color: "var(--color-cyan-5)" },
    ],
    total: "totalOrders",
    lowerColor: "var(--color-text-secondary)",
    lowerFill: "var(--color-subtle)",
  },
};

const MAIN_HEIGHT = 210;
const MAIN_MARGIN_TOP = 6;
// Room under the baseline so an active dot on a zero value is not cut by the SVG edge.
const MAIN_MARGIN_BOTTOM = 6;
const CHART_RIGHT_MARGIN = 14;
const LOWER_HEIGHT = 48;
const X_AXIS_HEIGHT = 22;
const MIN_AXIS_WIDTH = 34;
const AXIS_TICK_MARGIN = 8;
// 11px tabular glyphs average about 6.6px; CJK units ("万") are wider, so count them double.
function axisWidth(labels: readonly string[]): number {
  const longest = Math.max(
    0,
    ...labels.map((label) => [...label].reduce((sum, char) => sum + (char > "\u2e80" ? 2 : 1), 0)),
  );
  // Ticks draw no tick line (tickSize 0), so the label plus its margin is the whole width.
  return Math.max(MIN_AXIS_WIDTH, Math.ceil(longest * 7) + AXIS_TICK_MARGIN + 4);
}

/** Organic = total − ad-attributed; unknown when either side is missing. */
function organic(total: number | null | undefined, ad: number | null | undefined): number | null {
  return total == null || ad == null ? null : Math.max(0, total - ad);
}

/**
 * One unit per chart: the selected view stacks ad and organic contribution on a
 * single axis, and its efficiency ratio gets its own panel below, sharing the
 * x-axis and hover. Picking another ratio swaps the lower panel only.
 */
export function PerformanceCard({
  trend,
  kpis,
  view,
  onViewChange,
  lowerMetric: lowerMetricPref,
  onLowerMetricChange,
  targetAcos = null,
  granularity,
  getLabel,
  isLoading = false,
  notice,
  salesAvailability = "available",
  className,
}: PerformanceCardProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const salesPending = salesAvailability !== "available";
  const lowerOptions = salesPending
    ? PERFORMANCE_LOWER_METRICS[view].filter((metric) => metric !== "tacos")
    : PERFORMANCE_LOWER_METRICS[view];
  // A persisted TACoS preference falls back to the view's default while sales are pending.
  const lowerMetric = lowerOptions.includes(lowerMetricPref)
    ? lowerMetricPref
    : (lowerOptions[0] ?? lowerMetricPref);
  const { locale, formatNumber } = useFormatters();
  const chartId = useId().replace(/:/g, "");
  const chartsRef = useRef<HTMLDivElement>(null);
  const [chartsWidth, setChartsWidth] = useState(0);
  const bandRef = useRef<HTMLDivElement>(null);
  const chartsMounted = !isLoading && trend.length > 0;
  // The charts mount only once data loads; measure them then and on every resize.
  useEffect(() => {
    const node = chartsRef.current;
    if (!chartsMounted || !node) return;
    setChartsWidth(node.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setChartsWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [chartsMounted]);
  const titleId = `${chartId}-title`;
  const config: ViewConfig = useMemo(() => {
    const base = VIEWS[view];
    if (!salesPending) return base;
    // Only the ad-attributed segment remains; it is also the stack's headline.
    const ad = base.stack.find(
      (segment): segment is StackSegment & { key: "sales" | "orders" } =>
        segment.key === "sales" || segment.key === "orders",
    );
    return ad ? { ...base, stack: [ad], total: ad.key } : base;
  }, [salesPending, view]);
  const kpiOf = useMemo(() => new Map(kpis.map((kpi) => [kpi.key, kpi])), [kpis]);
  const currency = kpiOf.get(config.total)?.currency ?? kpiOf.get("spend")?.currency;
  const formatValue = (metric: Metric, value: number | null | undefined) =>
    formatMetricValue(metric, value, kpiOf.get(metric)?.currency, { formatNumber });

  const rows: ChartRow[] = trend.map((point) => ({
    ...point,
    key: `${point.periodStart}/${point.periodEnd}`,
    // Seller Central totals cover seller shops only; in a mixed seller/vendor scope
    // subtract the sellers' own ad results, not the vendors' too (#679).
    organicSales: organic(
      point.totalSales,
      point.sellerSales !== undefined ? point.sellerSales : point.sales,
    ),
    organicOrders: organic(
      point.totalOrders,
      point.sellerOrders !== undefined ? point.sellerOrders : point.orders,
    ),
  }));
  const segmentValue = (row: ChartRow, key: StackSegment["key"]) => row[key] ?? 0;
  const mainMax = Math.max(
    0,
    ...rows.map((row) => {
      const stacked = config.stack.reduce(
        (sum, segment) => sum + segmentValue(row, segment.key),
        0,
      );
      return Math.max(stacked, config.line ? (row[config.line.key] ?? 0) : 0);
    }),
  );
  const mainFamily = METRIC_FAMILY[config.total];
  const mainTicks = niceTicks(mainMax, 2);
  const mainTickLabel = (value: number) =>
    formatAxisTick(mainFamily, value, currency, { formatNumber });

  const lowerAverage = kpiOf.get(lowerMetric)?.value ?? null;
  const reference = lowerMetric === "acos" && targetAcos != null ? targetAcos : lowerAverage;
  const lowerValues = rows.flatMap((row) => (row[lowerMetric] == null ? [] : [row[lowerMetric]]));
  const lowerBounds = [...lowerValues, ...(reference == null ? [] : [reference])];
  const lowerMin = Math.min(...lowerBounds);
  const lowerMax = Math.max(...lowerBounds);
  // Pad the band so the line and its reference never touch the panel edges.
  const lowerPad = (lowerMax - lowerMin) * 0.2 || Math.abs(lowerMax) * 0.1 || 1;
  const lowerDomain: [number, number] = lowerBounds.length
    ? [Math.max(0, lowerMin - lowerPad), lowerMax + lowerPad]
    : [0, 1];
  const referenceLabel = (value: number) => formatValue(lowerMetric, value);

  // Both panels reserve the same axis width so their bars and points line up.
  const yAxisWidth = axisWidth([
    ...mainTicks.map(mainTickLabel),
    ...(reference == null ? [] : [referenceLabel(reference)]),
  ]);

  const xTicks = pickXTicks(rows);
  const dayFormat = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const monthFormat = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  const tickLabel = (key: string) => {
    const start = key.split("/")[0] ?? key;
    const date = new Date(`${start}T00:00:00.000Z`);
    return granularity === "month" ? monthFormat.format(date) : dayFormat.format(date);
  };
  const tooltipLabel = (row: ChartRow) =>
    granularity === "day"
      ? new Intl.DateTimeFormat(locale, {
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        }).format(new Date(`${row.periodStart}T00:00:00.000Z`))
      : formatTrendPeriod(row.periodStart, row.periodEnd, locale);

  const segmentLabel = (key: StackSegment["key"]) =>
    key === "organicSales" || key === "organicOrders"
      ? t(`overview.performance.series.${key}`)
      : getLabel(key);
  const legend = [
    ...config.stack.map((segment) => ({
      key: segment.key,
      label: segmentLabel(segment.key),
      color: segment.color,
      shape: "square" as const,
    })),
    ...(config.line
      ? [
          {
            key: config.line.key,
            label: getLabel(config.line.key),
            color: config.line.color,
            shape: "line" as const,
          },
        ]
      : []),
  ];

  const tooltipFor = (row: ChartRow): PerformanceTooltipProps => {
    const total = row[config.total];
    const share = (value: number | null | undefined) =>
      value != null && total ? formatNumber(value / total, { style: "percent" }) : null;
    return {
      label: tooltipLabel(row),
      total: { name: getLabel(config.total), value: formatValue(config.total, total) },
      parts:
        config.stack.length > 1
          ? config.stack.map((segment) => {
              const value = row[segment.key];
              const isOrganic = segment.key === "organicSales" || segment.key === "organicOrders";
              return {
                key: segment.key,
                color: segment.color,
                shape: "square" as const,
                name: segmentLabel(segment.key),
                note: isOrganic ? share(value) : null,
                value: formatValue(
                  segment.key === "organicSales"
                    ? "totalSales"
                    : segment.key === "organicOrders"
                      ? "totalOrders"
                      : segment.key,
                  value,
                ),
              };
            })
          : [],
      extras: [
        ...(config.line
          ? [
              {
                key: config.line.key,
                color: config.line.color,
                shape: "line" as const,
                name: getLabel(config.line.key),
                note: null,
                value: formatValue(config.line.key, row[config.line.key]),
              },
            ]
          : []),
        {
          key: lowerMetric,
          color: config.lowerColor,
          shape: "line" as const,
          name: getLabel(lowerMetric),
          note: null,
          value: formatValue(lowerMetric, row[lowerMetric]),
        },
      ],
    };
  };

  const tableColumns: { key: Metric | StackSegment["key"]; label: string }[] = [
    { key: config.total, label: getLabel(config.total) },
    ...(config.stack.length > 1
      ? config.stack.map((segment) => ({ key: segment.key, label: segmentLabel(segment.key) }))
      : []),
    ...(config.line ? [{ key: config.line.key, label: getLabel(config.line.key) }] : []),
    { key: lowerMetric, label: getLabel(lowerMetric) },
  ];
  const tableValue = (row: ChartRow, key: Metric | StackSegment["key"]) =>
    key === "organicSales"
      ? formatValue("totalSales", row.organicSales)
      : key === "organicOrders"
        ? formatValue("totalOrders", row.organicOrders)
        : formatValue(key, row[key]);

  const topSegment = config.stack.at(-1)?.key;
  // The hover band highlights the bucket behind the bars only; the lower panel marks
  // the same bucket with its active dot. Recharts' own cursor would sit above the bars.
  const plotWidth = Math.max(0, chartsWidth - yAxisWidth - CHART_RIGHT_MARGIN);
  const bandWidth = rows.length ? Math.max(8, Math.min(24, (plotWidth / rows.length) * 0.9)) : 0;
  // Moves the band through its ref: a state update would re-render both charts on
  // every mouse move and make the band lag behind the tooltip.
  const trackHover = (state: MouseHandlerDataParam) => {
    const band = bandRef.current;
    if (!band) return;
    const x = state.activeTooltipIndex == null ? undefined : state.activeCoordinate?.x;
    if (x == null) {
      band.style.visibility = "hidden";
      return;
    }
    band.style.transform = `translateX(${x - bandWidth / 2}px)`;
    band.style.visibility = "visible";
  };
  const clearHover = () => {
    if (bandRef.current) bandRef.current.style.visibility = "hidden";
  };

  return (
    <section
      aria-labelledby={titleId}
      data-slot="performance-card"
      className={cn(
        "flex min-w-0 flex-col gap-3.5 rounded-lg bg-surface px-5 py-4 text-ink shadow-[var(--paper-raised-shadow)]",
        className,
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex items-center gap-3">
          <h2 id={titleId} className="text-base font-semibold text-ink">
            {t("overview.performance.title")}
          </h2>
          <SegmentControl<PerformanceView>
            size="xs"
            value={view}
            onValueChange={onViewChange}
            aria-label={t("overview.performance.viewLabel")}
          >
            {PERFORMANCE_VIEWS.map((option) => (
              <SegmentControlItem key={option} value={option}>
                {t(`overview.performance.views.${option}`)}
              </SegmentControlItem>
            ))}
          </SegmentControl>
        </div>
        <ul className="flex items-center gap-4 text-xs text-text-secondary">
          {legend.map((item) => (
            <li key={item.key} className="flex items-center gap-1.5">
              <SeriesSwatch color={item.color} shape={item.shape} />
              {item.label}
            </li>
          ))}
        </ul>
      </header>
      {notice}
      {isLoading ? (
        <Skeleton className="h-[310px] w-full rounded-md" />
      ) : rows.length === 0 ? (
        <div className="flex h-[310px] items-center justify-center text-sm text-muted-foreground">
          {t("overview.performance.empty")}
        </div>
      ) : (
        <div ref={chartsRef} className="relative isolate flex min-w-0 flex-col">
          <div
            ref={bandRef}
            aria-hidden
            className="pointer-events-none invisible absolute left-0 z-0 rounded-[4px] bg-subtle"
            style={{
              width: bandWidth,
              top: MAIN_MARGIN_TOP,
              height: MAIN_HEIGHT - MAIN_MARGIN_TOP - MAIN_MARGIN_BOTTOM,
            }}
          />
          <div
            className="relative"
            role="img"
            aria-label={t("overview.performance.chartAria", {
              series: legend.map((item) => item.label).join(", "),
              buckets: rows.length,
            })}
          >
            <ChartContainer
              config={{}}
              className="aspect-auto w-full min-w-0 text-badge tabular-nums"
              style={{ height: MAIN_HEIGHT }}
              initialDimension={{ width: 698, height: MAIN_HEIGHT }}
            >
              <ComposedChart
                data={rows}
                syncId={chartId}
                margin={{
                  top: MAIN_MARGIN_TOP,
                  right: CHART_RIGHT_MARGIN,
                  bottom: MAIN_MARGIN_BOTTOM,
                  left: 0,
                }}
                barCategoryGap="40%"
                onMouseMove={trackHover}
                onMouseLeave={clearHover}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--color-line)"
                  strokeDasharray="2 4"
                  horizontalValues={mainTicks.slice(1)}
                />
                <XAxis dataKey="key" hide />
                <YAxis
                  domain={[0, mainTicks.at(-1) ?? 1]}
                  ticks={mainTicks}
                  tickFormatter={mainTickLabel}
                  tickLine={false}
                  tickSize={0}
                  axisLine={false}
                  tickMargin={AXIS_TICK_MARGIN}
                  width={yAxisWidth}
                  tick={{ fontSize: 11 }}
                />
                <ReferenceLine y={0} stroke="var(--color-line)" />
                <ChartTooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    const row = payload?.[0]?.payload as ChartRow | undefined;
                    if (!active || !row) return null;
                    return <PerformanceTooltip {...tooltipFor(row)} />;
                  }}
                />
                {config.stack.map((segment) => (
                  <Bar
                    key={segment.key}
                    dataKey={segment.key}
                    stackId="main"
                    fill={segment.color}
                    maxBarSize={12}
                    radius={segment.key === topSegment ? [2, 2, 0, 0] : 0}
                    isAnimationActive={false}
                  />
                ))}
                {config.line ? (
                  <>
                    {/* Surface-colored halo keeps the line legible where it crosses the bars. */}
                    <Line
                      dataKey={config.line.key}
                      stroke="var(--color-surface)"
                      strokeWidth={5}
                      dot={false}
                      activeDot={false}
                      tooltipType="none"
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey={config.line.key}
                      stroke={config.line.color}
                      strokeWidth={2}
                      strokeLinecap="round"
                      dot={false}
                      activeDot={{
                        r: 3.5,
                        fill: "var(--color-surface)",
                        stroke: config.line.color,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </>
                ) : null}
              </ComposedChart>
            </ChartContainer>
          </div>

          <div className="relative mt-2 flex items-center justify-between gap-3 border-t border-subtle pt-3">
            {/* Title-level control: aligned with the card title, not the plot area. */}
            <LowerMetricSelect
              value={lowerMetric}
              options={lowerOptions}
              getLabel={getLabel}
              onValueChange={onLowerMetricChange}
            />
            <div className="flex min-w-0 items-center gap-3 text-xs">
              {lowerAverage != null ? (
                <span className="truncate tabular-nums text-muted-foreground">
                  {t("overview.performance.average", { value: referenceLabel(lowerAverage) })}
                </span>
              ) : null}
              {lowerMetric === "acos" && targetAcos != null ? (
                <span className="flex shrink-0 items-center gap-1.5 text-text-secondary">
                  <span
                    aria-hidden
                    className="w-3 border-t-[1.5px] border-dashed border-muted-foreground"
                  />
                  {t("overview.performance.target", { value: referenceLabel(targetAcos) })}
                </span>
              ) : null}
            </div>
          </div>

          <ChartContainer
            config={{}}
            className="relative aspect-auto w-full min-w-0 text-badge tabular-nums"
            style={{ height: LOWER_HEIGHT + X_AXIS_HEIGHT + 8 }}
            initialDimension={{ width: 698, height: LOWER_HEIGHT + X_AXIS_HEIGHT + 8 }}
          >
            <ComposedChart
              data={rows}
              syncId={chartId}
              margin={{ top: 8, right: CHART_RIGHT_MARGIN, bottom: 0, left: 0 }}
              barCategoryGap="40%"
              onMouseMove={trackHover}
              onMouseLeave={clearHover}
            >
              <XAxis
                dataKey="key"
                ticks={xTicks}
                tickFormatter={tickLabel}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                height={X_AXIS_HEIGHT}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                domain={lowerDomain}
                ticks={reference == null ? [] : [reference]}
                tickFormatter={referenceLabel}
                tickLine={false}
                tickSize={0}
                axisLine={false}
                tickMargin={AXIS_TICK_MARGIN}
                width={yAxisWidth}
                tick={{ fontSize: 11 }}
              />
              {/* The main panel owns the tooltip; this one only syncs the hovered bucket. */}
              <Tooltip cursor={false} content={() => null} />
              {/* Invisible bars give this panel the same category bands as the main panel,
                  so its points sit at bucket centers right under the bars above. */}
              <Bar
                dataKey={lowerMetric}
                fill="transparent"
                maxBarSize={12}
                activeBar={false}
                legendType="none"
                tooltipType="none"
                isAnimationActive={false}
              />
              {reference != null ? (
                <ReferenceLine
                  y={reference}
                  stroke="var(--color-muted-foreground)"
                  strokeDasharray="3 3"
                />
              ) : null}
              <Area
                dataKey={lowerMetric}
                baseValue={reference ?? "dataMin"}
                type="linear"
                stroke={config.lowerColor}
                strokeWidth={2}
                strokeLinecap="round"
                fill={config.lowerFill}
                fillOpacity={1}
                dot={false}
                activeDot={{
                  r: 3.5,
                  fill: "var(--color-surface)",
                  stroke: config.lowerColor,
                  strokeWidth: 2,
                }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>

          <table className="sr-only">
            <caption>{t("overview.performance.tableCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("overview.performance.period")}</th>
                {tableColumns.map((column) => (
                  <th key={column.key} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <th scope="row">{tooltipLabel(row)}</th>
                  {tableColumns.map((column) => (
                    <td key={column.key}>{tableValue(row, column.key)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Lower panel title; lists only the ratios that fit the view. */
function LowerMetricSelect({
  value,
  options,
  getLabel,
  onValueChange,
}: {
  value: Metric;
  options: readonly Metric[];
  getLabel: (metric: Metric) => string;
  onValueChange: (metric: Metric) => void;
}): React.ReactElement {
  const { t } = useTranslation("dashboard");
  return (
    <Select<Metric>
      value={value}
      onValueChange={(next) => {
        if (next && next !== value) onValueChange(next);
      }}
    >
      <SelectTrigger
        size="xs"
        variant="ghost"
        aria-label={t("overview.performance.changeLowerMetric", { metric: getLabel(value) })}
        // Offsets the ghost trigger's inner padding so the label lines up with the card title.
        className="-ml-1.5 text-ink"
      >
        {getLabel(value)}
      </SelectTrigger>
      <SelectContent size="compact" align="start">
        {options.map((metric) => (
          <SelectItem key={metric} value={metric}>
            {getLabel(metric)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SeriesSwatch({
  color,
  shape,
}: {
  color: string;
  shape: "square" | "line";
}): React.ReactElement {
  return shape === "line" ? (
    <span aria-hidden className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: color }} />
  ) : (
    <span aria-hidden className="size-2 shrink-0 rounded-[2px]" style={{ background: color }} />
  );
}

/** At most five evenly spaced labels, always ending on the last bucket. */
function pickXTicks(rows: readonly ChartRow[]): string[] {
  if (rows.length <= 5) return rows.map((row) => row.key);
  const step = Math.ceil((rows.length - 1) / 4);
  const keys: string[] = [];
  for (let index = 0; index < rows.length - 1; index += step) {
    const row = rows[index];
    if (row) keys.push(row.key);
  }
  // Drop a label crowding the final one.
  if (rows.length - 1 - (keys.length - 1) * step < step / 2) keys.pop();
  const last = rows.at(-1);
  if (last) keys.push(last.key);
  return keys;
}

interface PerformanceTooltipRow {
  key: string;
  color: string;
  shape: "square" | "line";
  name: string;
  /** Muted suffix, e.g. the organic share. */
  note: string | null;
  value: string;
}

interface PerformanceTooltipProps {
  label: string;
  total: { name: string; value: string };
  parts: readonly PerformanceTooltipRow[];
  extras: readonly PerformanceTooltipRow[];
}

function PerformanceTooltip({
  label,
  total,
  parts,
  extras,
}: PerformanceTooltipProps): React.ReactElement {
  const row = (item: PerformanceTooltipRow) => (
    <div key={item.key} className="flex items-center gap-2 pl-0.5">
      <SeriesSwatch color={item.color} shape={item.shape} />
      <span className="min-w-0 grow truncate text-xs text-text-secondary">
        {item.name}
        {item.note ? <span className="text-muted-foreground"> {item.note}</span> : null}
      </span>
      <span className="shrink-0 text-xs tabular-nums text-ink">{item.value}</span>
    </div>
  );
  return (
    <div className="flex w-max min-w-[184px] max-w-[260px] flex-col gap-1.5 rounded-md bg-surface px-3 py-2.5 shadow-[var(--paper-flyout-shadow)]">
      <div className="text-badge tabular-nums text-muted-foreground">{label}</div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-ink">{total.name}</span>
        <span className="text-sm font-semibold tabular-nums text-ink">{total.value}</span>
      </div>
      {parts.map(row)}
      <div aria-hidden className="h-px shrink-0 bg-subtle" />
      {extras.map(row)}
    </div>
  );
}

const performancePrefsSchema = z.object({
  view: z.enum(PERFORMANCE_VIEWS),
  lower: z.object({
    sales: dashboardMetricSchema,
    orders: dashboardMetricSchema,
  }),
});
type PerformancePrefs = z.infer<typeof performancePrefsSchema>;

const DEFAULT_PERFORMANCE_PREFS: PerformancePrefs = {
  view: "sales",
  lower: {
    sales: PERFORMANCE_LOWER_METRICS.sales[0] ?? "acos",
    orders: PERFORMANCE_LOWER_METRICS.orders[0] ?? "cvr",
  },
};

/**
 * The chosen view and each view's lower-panel metric persist per browser, like the
 * KPI cards; a stored metric that no longer fits its view falls back to the default.
 */
export function usePerformanceChartPrefs(storageKey = "adpraxis:dashboard-performance:v1") {
  const [prefs, setPrefs] = useState<PerformancePrefs>(() => {
    const parsed = performancePrefsSchema.safeParse(parseJson(readStorage(storageKey)));
    if (!parsed.success) return DEFAULT_PERFORMANCE_PREFS;
    const lower = { ...parsed.data.lower };
    for (const view of PERFORMANCE_VIEWS) {
      if (!PERFORMANCE_LOWER_METRICS[view].includes(lower[view])) {
        lower[view] = DEFAULT_PERFORMANCE_PREFS.lower[view];
      }
    }
    return { view: parsed.data.view, lower };
  });
  useEffect(() => {
    writeStorage(storageKey, JSON.stringify(prefs));
  }, [prefs, storageKey]);
  const setView = useCallback(
    (view: PerformanceView) => setPrefs((previous) => ({ ...previous, view })),
    [],
  );
  const setLowerMetric = useCallback(
    (metric: Metric) =>
      setPrefs((previous) => ({
        ...previous,
        lower: { ...previous.lower, [previous.view]: metric },
      })),
    [],
  );
  return {
    view: prefs.view,
    lowerMetric: prefs.lower[prefs.view],
    setView,
    setLowerMetric,
  };
}

function parseJson(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readStorage(key: string): string | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    // Restricted browser contexts can deny storage access.
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The choice still applies for this session.
  }
}
